import React, { useLayoutEffect, useCallback } from 'react';
import { Form, Input, Button, Space, Card } from 'antd';
import { useNavigate } from 'react-router-dom';
import PATH from '@/config/paths/PATH';
import { signIn } from '@/services/user.service';
import useUser from '@/redux/hooks/useUser';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import './index.scss';
import { ROLES } from '@/hooks/useRole';

function Login() {
  const navigate = useNavigate();
  const { signInUser, updateUser, user } = useUser();

  const handleNavigate = useCallback(
    (roleId = user.role_id) => {
      if (roleId === ROLES.ADMIN || roleId === ROLES.DIGITAL) {
        navigate(PATH.REPORT.ROOT, { replace: true });
      } else if (roleId === ROLES.CUSTOMER_CARE) {
        navigate(PATH.CUSTOMER_CARE.ROOT, { replace: true });
      }
    },
    [navigate, user.role_id]
  );

  const loginReq = useMutation({
    mutationFn: (values) => signIn(values),
    onSuccess: (data) => {
      signInUser({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
      });
      updateUser({
        id: data.id,
        username: data.username,
        fullname: data.fullname,
        role_id: data.role_id,
      });

      handleNavigate(data.role_id);
    },
    onError: (err) => {
      const { data } = err;
      if (data) {
        toast.error(data?.errorMessage || 'An error occurred during login');
      } else {
        toast.error(err.message || 'An error occurred during login');
      }
    },
  });

  const onFinish = async (values) => {
    loginReq.mutate(values);
  };

  useLayoutEffect(() => {
    if (user.isAuthenticated) {
      handleNavigate();
    }
  }, [user.isAuthenticated, handleNavigate]);

  return (
    <div className="min-h-[100dvh] flex items-center justify-center bg-[#f8f9f9]">
      <Card className="login-card">
        <div className="w-full justify-between flex items-center">
          <Space className="w-[90dvw] sm:w-[480px] px-8 p-12 sm:p-12 sm:px-12" direction="vertical">
            <Space className="w-full justify-center items-center mb-6">
              <img src="/logo.png" alt="CMS Magic Wheel" className="aspect-auto h-auto max-w-36" />
            </Space>
            <Form
              name="login"
              onFinish={onFinish}
              layout="vertical"
              requiredMark={false}
              disabled={loginReq.isPending}
            >
              <Form.Item
                name="username"
                rules={[{ required: true, message: 'Please enter your username!' }]}
                label={<span className="font-semibold">Username</span>}
              >
                <Input size="large" placeholder="Username" />
              </Form.Item>

              <Form.Item
                name="password"
                rules={[{ required: true, message: 'Please enter your password!' }]}
                label={<span className="font-semibold">Password</span>}
              >
                <Input.Password size="large" placeholder="Password" />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  block
                  loading={loginReq.isPending}
                  iconPosition="end"
                  className="font-semibold"
                >
                  Sign in
                </Button>
              </Form.Item>
            </Form>
          </Space>
        </div>
      </Card>
    </div>
  );
}

export default Login;
